import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiHeart,
    FiShoppingCart,
    FiArrowLeft,
    FiPlus,
    FiMinus,
    FiTruck,
    FiTrash2,
    FiPackage,
    FiChevronLeft
} from "react-icons/fi";
import { useCart } from "../App";
import ProductCard from "./ProductCard";

export default function Wishlist() {
    const navigate = useNavigate();
    const {
        wishlist = [],
        cart = [],
        addToCart,
        toggleWishlistItem,
        removeFromWishlist,
        clearWishlist,
        updateQty,
        removeFromCart
    } = useCart();

    // Get cart quantities
    const cartQuantities = cart.reduce((acc, item) => {
        acc[item._id] = item.qty || 1;
        return acc;
    }, {});

    const moveToCart = (product) => {
        if (addToCart) {
            // Prepare cart item
            const cartItem = {
                _id: product._id || product.id,
                name: product.name,
                price: product.price,
                originalPrice: product.originalPrice,
                image: product.image,
                category: product.category,
                weight: product.weight,
                qty: 1
            };

            addToCart(cartItem, 1);
            // Remove from wishlist
            if (removeFromWishlist) {
                removeFromWishlist(product._id || product.id);
            }

            // Show success message
            const event = new CustomEvent('showNotification', {
                detail: {
                    message: 'Added to cart!',
                    type: 'success'
                }
            });
            window.dispatchEvent(event);
        }
    };

    const handleRemoveWishlist = (product) => {
        if (toggleWishlistItem) {
            // Toggle will remove if it exists
            toggleWishlistItem(product);
        } else if (removeFromWishlist) {
            removeFromWishlist(product._id || product.id);
        }

        // Show removal message
        const event = new CustomEvent('showNotification', {
            detail: {
                message: 'Removed from wishlist',
                type: 'info'
            }
        });
        window.dispatchEvent(event);
    };

    const handleIncrement = (productId) => {
        if (updateQty) {
            updateQty(productId, (cartQuantities[productId] || 0) + 1);
        }
    };

    const handleDecrement = (productId) => {
        if (removeFromCart) {
            const currentQty = cartQuantities[productId] || 0;
            if (currentQty <= 1) {
                removeFromCart(productId);
            } else {
                if (updateQty) updateQty(productId, currentQty - 1);
            }
        }
    };

    const handleClearWishlist = () => {
        if (clearWishlist) {
            if (window.confirm("Are you sure you want to clear your entire wishlist?")) {
                clearWishlist();

                // Show clear message
                const event = new CustomEvent('showNotification', {
                    detail: {
                        message: 'Wishlist cleared',
                        type: 'info'
                    }
                });
                window.dispatchEvent(event);
            }
        }
    };

    const moveAllToCart = () => {
        if (wishlist.length > 0) {
            wishlist.forEach(product => {
                if (product.stock > 0) {
                    const cartItem = {
                        _id: product._id || product.id,
                        name: product.name,
                        price: product.price,
                        originalPrice: product.originalPrice,
                        image: product.image,
                        category: product.category,
                        weight: product.weight,
                        qty: 1
                    };
                    addToCart(cartItem, 1);
                }
            });
            
            clearWishlist();
            
            const event = new CustomEvent('showNotification', {
                detail: {
                    message: 'All available items moved to cart!',
                    type: 'success'
                }
            });
            window.dispatchEvent(event);
        }
    };

    const normalizeWishlistProduct = (item) => ({
        id: item._id || item.id,
        _id: item._id || item.id,
        slug: item.slug || item.name,
        name: item.name,
        image: item.image || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400",
        price: item.price || 0,
        originalPrice: item.originalPrice || 0,
        discount: item.discount || 0,
        stock: item.stock || 0,
        category: item.category || "Wellness",
        rating: item.rating || 4.2,
        reviewCount: item.reviewCount || 0,
        weight: item.weight || "100g"
    });

    const handleWishlistQuantityUpdate = (productId, delta) => {
        if (delta > 0) {
            handleIncrement(productId);
        } else {
            handleDecrement(productId);
        }
    };

    if (!wishlist || wishlist.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 pb-24">
                {/* Header */}
                <div className="bg-white sticky top-0 z-30 border-b shadow-sm">
                    <div className="container mx-auto px-4 py-4">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-2 text-gray-700 hover:text-red-600"
                            >
                                <FiChevronLeft />
                                <span className="hidden sm:inline">Back</span>
                            </button>
                            <h1 className="text-lg font-bold text-gray-900">Wishlist</h1>
                            <div className="w-6" />
                        </div>
                    </div>
                </div>

                {/* Empty State */}
                <div className="container mx-auto px-4 py-8">
                    <div className="max-w-md mx-auto text-center py-12">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200 }}
                            className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6"
                        >
                            <FiHeart className="text-red-500 text-3xl" />
                        </motion.div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Your Wishlist is Empty</h1>
                        <p className="text-gray-600 mb-8">
                            Save your favorite products here for easy access.
                            Click the ♡ icon on any product to add it here.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={() => navigate("/products")}
                                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-colors"
                            >
                                Browse Products
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                className="px-6 py-3 border border-red-600 text-red-600 font-semibold rounded-full hover:bg-red-50 transition-colors"
                            >
                                Go Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white sticky top-0 z-30 border-b shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-700 hover:text-red-600"
                        >
                            <FiChevronLeft />
                            <span className="hidden sm:inline">Back</span>
                        </button>
                        <h1 className="text-lg font-bold text-gray-900">Wishlist ({wishlist.length})</h1>
                        <button
                            onClick={handleClearWishlist}
                            className="text-red-600 text-sm font-medium hover:text-red-700"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-6 pb-24">
                {/* Bulk Actions Bar */}
                <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <FiHeart className="text-red-500" />
                            <span className="font-medium text-gray-900">
                                {wishlist.length} item{wishlist.length !== 1 ? 's' : ''} saved
                            </span>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={moveAllToCart}
                                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                                <FiShoppingCart size={16} />
                                <span>Move All to Cart</span>
                            </button>
                            <button
                                onClick={handleClearWishlist}
                                className="px-4 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                            >
                                <FiTrash2 size={16} />
                                <span>Clear All</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Wishlist Products Grid - styled to match the homepage product card */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <AnimatePresence>
                        {wishlist.map((item) => {
                            const product = normalizeWishlistProduct(item);
                            return (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <ProductCard
                                        product={product}
                                        onAddToCart={moveToCart}
                                        onToggleWishlist={handleRemoveWishlist}
                                        onQuantityUpdate={handleWishlistQuantityUpdate}
                                        isWishlisted={true}
                                        cartQuantity={cartQuantities[product.id] || 0}
                                        navigate={navigate}
                                    />
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>

                {/* Summary - Added for better UX */}
                {/* <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-center sm:text-left">
                            <p className="font-medium text-gray-900">
                                Wishlist Value: <span className="text-red-600">
                                    ₹{wishlist.reduce((total, item) => total + (item.price || 0), 0).toLocaleString()}
                                </span>
                            </p>
                            <p className="text-sm text-gray-600">
                                {wishlist.length} items • {wishlist.filter(item => cartQuantities[item._id]).length} already in cart
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate("/products")}
                                className="px-6 py-2.5 border border-red-600 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
                            >
                                <FiPackage size={16} />
                                <span>Shop More</span>
                            </button>
                            <button
                                onClick={() => navigate("/cart")}
                                className="px-6 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                                <FiShoppingCart size={16} />
                                <span>View Cart ({cart.filter(item => wishlist.some(w => w._id === item._id)).length})</span>
                            </button>
                        </div>
                    </div>
                </div> */}

                {/* Quick Stats */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                    <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-xs text-red-700 font-medium">Items in Cart</p>
                        <p className="text-lg font-bold text-red-900">
                            {wishlist.filter(item => cartQuantities[item._id]).length}
                        </p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-xs text-red-700 font-medium">Only in Wishlist</p>
                        <p className="text-lg font-bold text-red-900">
                            {wishlist.filter(item => !cartQuantities[item._id]).length}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}